import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const flagFor = (location = '') => {
  const loc = location.toLowerCase();
  if (/cyprus|paphos|nicosia|larnaca|limassol|kyrenia|chloraka/.test(loc)) return '🇨🇾';
  if (/uk|united kingdom|england|scotland|wales|northern ireland|london|manchester|edinburgh|cardiff|brighton|bristol|birmingham|leeds|liverpool|glasgow|belfast|oxford|cambridge/.test(loc))
    return '🇬🇧';
  return '✦';
};

function StarRow({ value }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-3.5 h-3.5 ${
            n <= value ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#CAD2C5]'
          }`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function HomepageReviews() {
  const [reviews, setReviews] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/reviews/recent`, { params: { limit: 9 } })
      .then((r) => {
        if (!cancelled) setReviews(r.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Group into pages of 3 for desktop
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < reviews.length; i += 3) out.push(reviews.slice(i, i + 3));
    return out;
  }, [reviews]);

  // Auto-rotate every 6s
  useEffect(() => {
    if (pages.length <= 1) return undefined;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % pages.length);
    }, 6000);
    return () => clearInterval(t);
  }, [pages.length]);

  const average = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  }, [reviews]);

  if (reviews.length === 0) return null;

  return (
    <section
      className="section-padding bg-[#F5F5F0]"
      data-testid="homepage-reviews"
    >
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12 lg:mb-16">
          <p className="text-caption text-[#84A98C] mb-4">Real Reviews · Cyprus &amp; UK</p>
          <h2 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Customer love, one bead at a time
          </h2>
          <div className="inline-flex items-center gap-3 mt-2">
            <StarRow value={Math.round(average)} />
            <span className="text-sm text-[#52796F]">
              {average.toFixed(1)} out of 5 · {reviews.length}+ verified reviews
            </span>
          </div>
        </div>

        {/* Page indicator pills */}
        {pages.length > 1 && (
          <div className="flex justify-center gap-2 mb-8" data-testid="reviews-pagination">
            {pages.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(i)}
                className={`h-1 rounded-full transition-all ${
                  i === activeIdx ? 'w-8 bg-[#D4AF37]' : 'w-3 bg-[#CAD2C5] hover:bg-[#84A98C]'
                }`}
                aria-label={`Show reviews page ${i + 1}`}
                data-testid={`reviews-page-${i}`}
              />
            ))}
          </div>
        )}

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {(pages[activeIdx] || []).map((r) => (
            <article
              key={r.id}
              className="bg-[#FAF9F6] p-7 lg:p-8 rounded-sm shadow-soft flex flex-col gap-4 relative animate-in fade-in"
              data-testid={`homepage-review-${r.id}`}
            >
              <Quote className="absolute top-4 right-5 w-7 h-7 text-[#D4AF37]/20" strokeWidth={1.5} />

              <div className="flex items-center gap-3">
                <StarRow value={r.rating || 5} />
              </div>

              <p className="text-[#52796F] leading-relaxed font-serif italic relative z-10">
                "{r.text}"
              </p>

              <div className="mt-auto pt-3 border-t border-[#CAD2C5]/40 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-[#354F52]">{r.author_name}</p>
                  {r.location && (
                    <p className="text-xs text-[#84A98C] mt-0.5 flex items-center gap-1.5">
                      <span aria-hidden>{flagFor(r.location)}</span>
                      {r.location}
                    </p>
                  )}
                </div>
                {r.product_id && (
                  <Link
                    to={`/products/${r.product_id}`}
                    className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#52796F] transition-colors text-right"
                    data-testid={`review-product-link-${r.id}`}
                  >
                    {r.product_name || 'View'} →
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/products"
            className="text-sm text-[#52796F] hover:text-[#D4AF37] tracking-widest uppercase transition-colors"
            data-testid="reviews-cta"
          >
            Shop the bracelets →
          </Link>
        </div>
      </div>
    </section>
  );
}
